import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone, Linkedin } from "lucide-react";
import { Reveal } from "./Reveal";
import { CONTACT } from "../../content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = ({ t }) => {
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(c.success);
      setForm({ name: "", email: "", organization: "", message: "" });
    } catch (err) {
      toast.error(c.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-line focus:border-green px-4 py-3.5 text-ink placeholder-muted outline-none focus:ring-1 focus:ring-green transition-colors duration-200";

  return (
    <section id="contact" className="py-24 md:py-32 lg:py-40" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-green mb-6">{c.eyebrow}</p>
              <h2 className="font-display font-medium text-3xl md:text-4xl lg:text-5xl text-ink tracking-tight leading-tight">
                {c.title}
              </h2>
              <p className="mt-8 text-base md:text-lg text-ink-2 leading-relaxed max-w-md">{c.lead}</p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="contact-form">
                <div>
                  <label className="block text-xs uppercase tracking-wider font-semibold text-ink-2 mb-2">{c.name}</label>
                  <input data-testid="contact-input-name" required value={form.name} onChange={upd("name")} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider font-semibold text-ink-2 mb-2">{c.email}</label>
                  <input data-testid="contact-input-email" required type="email" value={form.email} onChange={upd("email")} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs uppercase tracking-wider font-semibold text-ink-2 mb-2">
                    {c.org} <span className="text-muted normal-case tracking-normal">{c.orgOpt}</span>
                  </label>
                  <input data-testid="contact-input-org" value={form.organization} onChange={upd("organization")} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs uppercase tracking-wider font-semibold text-ink-2 mb-2">{c.message}</label>
                  <textarea data-testid="contact-input-message" required rows={5} value={form.message} onChange={upd("message")} className={`${inputCls} resize-none`} />
                </div>
                <div className="sm:col-span-2">
                  <button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={loading}
                    className="group inline-flex items-center gap-2 bg-green hover:bg-green-hover disabled:opacity-60 text-bone text-base font-medium px-8 py-4 transition-colors duration-300"
                  >
                    {loading ? c.sending : c.send}
                    {!loading && <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />}
                  </button>
                </div>
                <p className="sm:col-span-2 text-xs text-muted leading-relaxed" data-testid="contact-privacy-note">
                  {c.privacyNote}{" "}
                  <a href="/datenschutz.html" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-ink transition-colors" data-testid="form-privacy-link">{c.privacyLink}</a>
                </p>
              </form>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-10 border-t border-line pt-8" data-testid="contact-direct">
                <p className="text-xs uppercase tracking-[0.22em] font-bold text-muted mb-5">{c.directLabel}</p>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 sm:gap-x-10 sm:gap-y-4">
                  <a data-testid="contact-email" href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-ink hover:text-green transition-colors">
                    <Mail size={17} className="text-accent" /> <span className="link-underline pb-0.5">{CONTACT.email}</span>
                  </a>
                  <a data-testid="contact-phone" href={CONTACT.phoneHref} className="flex items-center gap-3 text-ink hover:text-green transition-colors">
                    <Phone size={17} className="text-accent" /> <span className="link-underline pb-0.5">{CONTACT.phone}</span>
                  </a>
                  <a data-testid="contact-linkedin" href={CONTACT.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-ink hover:text-green transition-colors">
                    <Linkedin size={17} className="text-accent" /> <span className="link-underline pb-0.5">LinkedIn</span>
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Footer = ({ t }) => (
  <footer className="bg-green text-bone" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
        <div>
          <div className="font-display text-3xl md:text-4xl">Timo Schewik<span className="text-accent-light">.</span></div>
          <p className="mt-3 text-bone/70 max-w-sm text-sm md:text-base">{t.footer.tagline}</p>
        </div>
        <div className="flex flex-col sm:items-end gap-1 text-sm text-bone/80">
          <a href={`mailto:${CONTACT.email}`} className="hover:text-bone transition-colors">{CONTACT.email}</a>
          <a href={CONTACT.phoneHref} className="hover:text-bone transition-colors">{CONTACT.phone}</a>
        </div>
      </div>
      <div className="mt-12 pt-6 border-t border-bone/20 text-xs text-bone/55">
        <span>© {new Date().getFullYear()} {CONTACT.name}. {t.footer.rights}</span>
      </div>
    </div>
  </footer>
);
